import {
  CONTACT_EMAIL,
  CONTACT_PHONE_E164,
  FACEBOOK_URL,
  LINKED_IN_URL,
  SITE_URL,
  X_URL,
  YOUTUBE_URL,
} from "~/config/constants";
import type { LanguageContextType } from "~/context/LanguageContext";
import { localizeUrl } from "~/helpers/navigationHelper";

type Lang = LanguageContextType["lang"];

const ORG_ID = `${SITE_URL}/#organization`;
const SITE_ID = `${SITE_URL}/#website`;

/**
 * Emitted once, from the root route. Every other schema on the site references
 * the organisation by @id rather than repeating it.
 */
export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "EducationalOrganization",
        "@id": ORG_ID,
        name: "SkillSynergy",
        url: SITE_URL,
        logo: `${SITE_URL}/logo.svg`,
        email: CONTACT_EMAIL,
        address: {
          "@type": "PostalAddress",
          addressLocality: "Berlin",
          addressCountry: "DE",
        },
        contactPoint: {
          "@type": "ContactPoint",
          telephone: CONTACT_PHONE_E164,
          contactType: "Customer Support",
          email: CONTACT_EMAIL,
          availableLanguage: ["English", "French", "German", "Arabic"],
        },
        sameAs: [LINKED_IN_URL, X_URL, YOUTUBE_URL, FACEBOOK_URL],
      },
      {
        "@type": "WebSite",
        "@id": SITE_ID,
        url: `${SITE_URL}/`,
        name: "SkillSynergy",
        publisher: { "@id": ORG_ID },
        inLanguage: ["en", "fr", "de", "ar"],
      },
    ],
  };
}

/**
 * A Course needs `hasCourseInstance` with a mode, and `offers` with a real
 * price and currency, or Google drops it from the rich result.
 */
export function courseSchema(input: {
  lang: Lang;
  slug: string;
  title: string;
  description: string;
  price: number;
  currency: string;
}) {
  const url = localizeUrl(input.lang, `/course/${input.slug}`);

  return {
    "@context": "https://schema.org",
    "@type": "Course",
    "@id": `${url}#course`,
    name: input.title,
    description: input.description,
    url,
    inLanguage: input.lang,
    provider: { "@id": ORG_ID },
    hasCourseInstance: {
      "@type": "CourseInstance",
      courseMode: "Online",
      courseWorkload: "PT8H",
      inLanguage: input.lang,
    },
    offers: {
      "@type": "Offer",
      url,
      price: input.price,
      priceCurrency: input.currency,
      category: "Online Course",
      availability: "https://schema.org/InStock",
    },
  };
}

export function faqSchema(items: { q: string; a: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };
}

/** A real trail for this page, not the static site-wide nav list. */
export function breadcrumbSchema(
  lang: Lang,
  trail: { name: string; path: string }[],
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: localizeUrl(lang, crumb.path),
    })),
  };
}
